/**
 * HTTP client for kiosk-api.
 *
 * Attaches Authorization + X-Device-Id headers on every request.
 * Surfaces X-Re-Key-Required-At header to the caller via response metadata.
 */

import { secureStorage } from "./secure-storage";

// Injected by the build; falls back to the dev endpoint for `pnpm dev`.
const API_BASE =
  (import.meta.env["VITE_KIOSK_API_URL"] as string | undefined) ??
  "https://kiosk-api.dev.apps.myfinterra.com";

export interface KioskResponse<T> {
  data: T;
  reKeyRequiredAt: string | null;
}

export class KioskApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`kiosk-api ${status}`);
    this.name = "KioskApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  includeAuth = true,
): Promise<KioskResponse<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };

  if (includeAuth) {
    const token = secureStorage.getItem("access_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const deviceId = secureStorage.getItem("device_id");
    if (deviceId) headers["X-Device-Id"] = deviceId;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  const reKeyRequiredAt = res.headers.get("X-Re-Key-Required-At");

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new KioskApiError(res.status, body);
  }

  const data = (await res.json()) as T;
  return { data, reKeyRequiredAt };
}

export const kioskClient = {
  /** No auth — first-boot claim. */
  claimDevice(provisionCode: string) {
    return request<{
      device_id: string;
      location_id: string;
      access_token: string;
      refresh_token: string;
      access_token_expires_in: number;
      refresh_token_expires_at: string;
    }>(
      "/kiosk/v1/devices/claim",
      {
        method: "POST",
        body: JSON.stringify({ provision_code: provisionCode }),
      },
      false,
    );
  },

  /** Refresh token auth. */
  refreshTokens(refreshToken: string) {
    return request<{
      device_id: string;
      access_token: string;
      refresh_token: string;
      access_token_expires_in: number;
      refresh_token_expires_at: string;
    }>(
      "/kiosk/v1/devices/refresh",
      {
        method: "POST",
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
      false,
    );
  },

  heartbeat() {
    return request<{ ok: boolean }>("/kiosk/v1/devices/heartbeat", {
      method: "POST",
    });
  },

  bootstrap() {
    return request<BootstrapPayload>("/kiosk/v1/bootstrap");
  },

  getPaymentRails() {
    return request<PaymentRail[]>("/kiosk/v1/payment-rails");
  },

  getDepartments() {
    return request<Department[]>("/kiosk/v1/departments");
  },

  createOrder(body: {
    amount_cents: number;
    department_id?: string;
    external_reference?: string;
  }) {
    return request<{ order_id: string; status: string; amount_cents: number }>(
      "/kiosk/v1/orders",
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  getOrder(orderId: string) {
    return request<Order>(`/kiosk/v1/orders/${orderId}`);
  },

  createPaymentLink(orderId: string, featurePath: string) {
    return request<{
      order_id: string;
      checkout_url: string;
      amount_cents: number;
      expires_in: number;
    }>(`/kiosk/v1/orders/${orderId}/payment-link`, {
      method: "POST",
      body: JSON.stringify({ feature_path: featurePath }),
    });
  },

  claimHandoff(token: string) {
    return request<{ order: Order; amount_due_cents: number }>(
      "/kiosk/v1/orders/handoff/claim",
      { method: "POST", body: JSON.stringify({ token }) },
    );
  },

  sendSmsReceipt(orderId: string, phone: string) {
    return request<{ ok: boolean }>(`/kiosk/v1/orders/${orderId}/receipt/sms`, {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
  },

  adminUnlock(pin: string) {
    return request<{ ok: boolean }>("/kiosk/v1/admin-unlock", {
      method: "POST",
      body: JSON.stringify({ pin }),
    });
  },
};

export interface BootstrapPayload {
  partner_slug: string | null;
  brand_config: BrandConfig;
  languages: string[];
  default_language: string;
  payment_methods: PaymentRail[];
  idle_media: IdleMediaItem[];
  idle_loop_interval_seconds: number;
  idle_timeout_seconds: number;
}

export interface BrandConfig {
  primary_color?: string;
  accent_color?: string;
  background_color?: string;
  surface_color?: string;
  text_color?: string;
  muted_color?: string;
  border_radius?: string;
  logo_url?: string;
  partner_name?: string;
}

export interface PaymentRail {
  feature_path: string;
  display_name: string;
  provider_slug: string;
}

export interface Department {
  id: string;
  name: string;
}

export interface Order {
  id: string;
  location_id: string;
  channel: string;
  status: string;
  amount_cents: number;
  paid_cents: number;
  external_reference: string | null;
  created_at: string;
  updated_at: string;
}

export interface IdleMediaItem {
  url: string;
  filename: string;
  content_type: string;
  sort_order: number;
}
